// Durable offline write queue ("outbox").
//
// When the app can't reach the server, mutations are serialized and persisted
// here (IndexedDB via localforage) so they survive reloads and app restarts,
// then replayed FIFO once connectivity returns. This is the storage + replay
// engine; the reactive Vuex facade lives in src/state/modules/outbox.js and the
// call-site entry point is src/plugins/offline-mutate.js.
//
// Idempotency: every queued item carries a client-generated `idempotencyKey`
// which is also injected into the mutation variables. The current backend does
// NOT yet honor it (no idempotencyKey field in the GraphQL schema), so replay
// is at-least-once: if a mutation succeeds server-side but its response is lost
// (connection drops at the wrong instant), a create can be submitted twice. The
// key is sent now so that the moment the backend dedupes on it, that gap closes
// with no frontend change. Edits/enable/disable/reset are naturally idempotent
// and unaffected.
import localforage from 'localforage'
import { gql } from '@apollo/client/core'
import { reportError } from '@utils/report-error'
import { offlineSyncConfig } from './offline-config'
import { notifyOfflineQueues } from './offline-events'

// Hard cap on the queue so a long offline stint (or a persistent flush failure)
// can't grow storage without bound. Reaching it is surfaced to the caller and
// reported. Never a silent drop.
export const MAX_QUEUED_MUTATIONS = 500

const store = localforage.createInstance({
  name: 'bowvue',
  storeName: 'mutation_outbox',
  description: 'Queued GraphQL mutations awaiting sync when back online',
})

// Maps temporary client-side ids (e.g. a `pending:<uuid>` storeId handed out by
// the offline upload queue, or a temp entity id) to the real server id learned
// once the dependency syncs. Applied to a mutation's variables just before it
// replays, so a metadata mutation queued offline with a placeholder id ends up
// referencing the real one. Persisted so it survives reloads mid-sync.
const idMapStore = localforage.createInstance({
  name: 'bowvue',
  storeName: 'id_map',
  description: 'Temporary→real id reconciliation for queued mutations',
})

export async function registerId(tempId, realId) {
  await idMapStore.setItem(tempId, realId)
}

async function loadIdMap() {
  const map = {}
  await idMapStore.iterate((value, key) => {
    map[key] = value
  })
  return map
}

// Recursively substitute any string value that matches a known temp id. Handles
// nested objects and arrays (e.g. ADD_INFRARED_SURVEY_PHOTOS sends a storeId
// array).
export function reconcileValue(value, map) {
  if (typeof value === 'string') {
    return Object.prototype.hasOwnProperty.call(map, value) ? map[value] : value
  }
  if (Array.isArray(value)) {
    return value.map((v) => reconcileValue(v, map))
  }
  // Serialize a Date the way JSON would. Items queued before enqueue's JSON
  // round-trip stored real Dates (IndexedDB keeps them); the object branch
  // below would flatten one to `{}` (a Date has no own keys), which the server
  // rejects as "time should be RFC3339Nano formatted string".
  if (value instanceof Date) {
    return value.toISOString()
  }
  if (value && typeof value === 'object') {
    const out = {}
    for (const k of Object.keys(value)) {
      out[k] = reconcileValue(value[k], map)
    }
    return out
  }
  return value
}

// Monotonic sequence guaranteeing FIFO replay order (IndexedDB iteration order
// is not guaranteed). Seeded from the persisted queue on startup via initSeq().
let seqCounter = 0

function uuid() {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

// Set `field` on the mutation's payload. Prefer nesting inside `input` (the
// app's mutations almost all take a single `input` object) so it rides along
// with the payload; fall back to a top-level variable otherwise.
function injectField(variables, field, value) {
  const v = { ...(variables || {}) }
  if (v.input && typeof v.input === 'object' && !Array.isArray(v.input)) {
    v.input = { ...v.input, [field]: value }
  } else {
    v[field] = value
  }
  return v
}

// Read a GraphQL error's machine-readable code (see the backend error contract
// in docs/offline-sync-backend-contract.md). Returns e.g. 'CONFLICT' or null.
function graphQLErrorCode(err) {
  const gqlErrors = (err && err.graphQLErrors) || []
  for (const e of gqlErrors) {
    if (e && e.extensions && e.extensions.code) return e.extensions.code
  }
  return null
}

// A refusal that is TEMPORARY BY CONSTRUCTION: the server said no for a reason
// that goes away on its own, with nothing for anyone to fix in between. The
// only one today is LOCKED_OUT, which the backend stamps when a queued LOTO
// edit lands on equipment somebody has since locked out: the procedures are
// frozen until the lockout is released, and then the very same mutation works.
//
// This is a THIRD outcome, not a flavour of failure, and the distinction is
// the whole point of the state. `failed` means "a human must look at this",
// so it is never retried by itself, it counts against the location's red
// badge, and the sync message tells the tech to fix it and press Sync again.
// Every one of those is wrong here: the tech cannot fix it, pressing Sync
// harder will not help, and the change is perfectly good. Parking it as
// failed also strands it behind an acknowledgement nobody thinks to give, so
// an edit made in the field would sit in the queue after the lockout cleared.
//
// A `blocked` item stays pending in every way that matters: it replays on the
// next flush with no human action, and it is the flush that decides whether
// it is still blocked.
function isTemporaryRefusal(err) {
  return graphQLErrorCode(err) === 'LOCKED_OUT'
}

// Extra data the server may attach to a conflict so the UI can offer a
// resolution (e.g. the current server version to overwrite past).
function conflictInfo(err) {
  const gqlErrors = (err && err.graphQLErrors) || []
  for (const e of gqlErrors) {
    if (e && e.extensions && e.extensions.code === 'CONFLICT') {
      return {
        message: e.message || 'This record was changed by someone else.',
        currentVersion: e.extensions.currentUpdatedAt || null,
      }
    }
  }
  return { message: 'Conflict', currentVersion: null }
}

// Walk a dot-path ('addInfraredSurvey.infraredsurvey.id') into a mutation
// response. Returns undefined when any hop is missing.
function readPath(obj, path) {
  let cur = obj
  for (const part of String(path).split('.')) {
    if (cur == null || typeof cur !== 'object') return undefined
    cur = cur[part]
  }
  return cur
}

// Every temp id a queued create stands for: its own, and, for one that makes
// several things at once (an Add Procedure: the procedure, its steps and their
// photos), each in idResponsePaths.
export function createdTempIds(item) {
  const ids = item.tempId ? [item.tempId] : []
  return ids.concat(Object.keys(item.idResponsePaths || {}))
}

// True if any value still looks like an unresolved placeholder id.
function hasUnresolvedTempId(value) {
  if (typeof value === 'string') return value.startsWith('pending:')
  if (Array.isArray(value)) return value.some(hasUnresolvedTempId)
  if (value && typeof value === 'object') {
    return Object.values(value).some(hasUnresolvedTempId)
  }
  return false
}

// Collect every `pending:` placeholder referenced anywhere in a value.
function collectPendingRefs(value, out) {
  if (typeof value === 'string') {
    if (value.startsWith('pending:')) out.add(value)
    return
  }
  if (Array.isArray(value)) {
    for (const v of value) collectPendingRefs(v, out)
    return
  }
  if (value && typeof value === 'object') {
    for (const v of Object.values(value)) collectPendingRefs(v, out)
  }
}

// Discard one queued item AND everything that depends on it. A discarded
// create's tempId will never resolve, so any queued mutation referencing it
// would hold forever, those go too, transitively. Returns:
//   removedIds, outbox item ids removed
//   removedTempIds, temp entity ids of removed creates (for snapshot cleanup)
//   uploadRefs, `pending:` refs in the removed items that belong to the
//                    upload queue, not to any outbox create (caller removes
//                    those uploads; this module doesn't own that store)
export async function discardItem(id) {
  const all = await allItems()
  const target = all.find((item) => item.id === id)
  if (!target) return { removedIds: [], removedTempIds: [], uploadRefs: [] }

  const toRemove = [target]
  const removedTempIds = new Set(createdTempIds(target))
  let grew = true
  while (grew) {
    grew = false
    for (const item of all) {
      if (toRemove.includes(item)) continue
      const refs = new Set()
      collectPendingRefs(item.variables, refs)
      if ([...refs].some((ref) => removedTempIds.has(ref))) {
        toRemove.push(item)
        for (const tempId of createdTempIds(item)) removedTempIds.add(tempId)
        grew = true
      }
    }
  }

  // A `pending:` ref that is not any outbox create's tempId is an upload
  // placeholder (`pending:<uuid>` from enqueueUpload).
  const createTempIds = new Set(all.flatMap(createdTempIds))
  const uploadRefs = new Set()
  for (const item of toRemove) {
    const refs = new Set()
    collectPendingRefs(item.variables, refs)
    for (const ref of refs) {
      if (!createTempIds.has(ref)) uploadRefs.add(ref)
    }
  }

  for (const item of toRemove) {
    await store.removeItem(item.id)
  }
  notifyOfflineQueues()
  return {
    removedIds: toRemove.map((item) => item.id),
    removedTempIds: [...removedTempIds],
    uploadRefs: [...uploadRefs],
  }
}

// WHAT KIND OF FAILURE THIS IS decides what a sync does about it:
//
//   offline   no response reached us: no signal, DNS, a dropped connection.
//             Stop; everything stays queued, in order, for the next Sync.
//   stalled   a photo upload stopped making progress (a weak signal that is
//             technically connected). Treated like offline, worded apart.
//   auth      the server answered 401: the session lapsed. Stop, keep the
//             change queued (nothing is wrong with it) and say to sign in.
//             401 ONLY: the backend answers a missing or rejected token with
//             401, so a 403 (from the API, or S3 refusing a signature) is a
//             refusal that signing in again would not fix.
//   server    5xx, 408, 429: the server could not take it right now. Stop and
//             keep it queued; a later Sync goes through.
//   rejected  the server answered and refused THIS change: a GraphQL error, or
//             a 400/422 schema rejection. Park it as failed for a human.
//
// THIS USED TO BE A YES/NO "is it a network error", and ANY Apollo
// networkError counted. Apollo reports an HTTP 401, 422 or 500 as a
// networkError too, so a server refusal was taken for "still offline": the
// item stayed pending and every Sync said the connection dropped, while the
// technician was standing in good signal. That is how a sync could look like
// it never sent anything, however many times Sync was pressed.
export function failureKind(err) {
  if (!err) return 'rejected'
  if (err.kind === 'offline' || err.kind === 'stalled') return err.kind
  const net = err.networkError
  if (net) {
    const status = net.statusCode || net.status
    if (status) return kindForStatus(status)
    // A networkError with no HTTP status is a request that never got an
    // answer: fetch's TypeError, a timeout.
    return 'offline'
  }
  if (err.graphQLErrors && err.graphQLErrors.length) return 'rejected'
  if (err.status) return kindForStatus(err.status)
  if (/network|failed to fetch|load failed/i.test(String(err.message || err))) {
    return 'offline'
  }
  return 'rejected'
}

function kindForStatus(status) {
  if (status === 401) return 'auth'
  if (status === 408 || status === 429 || status >= 500) return 'server'
  return 'rejected'
}

// "Try this again later" rather than "this change is refused". What decides
// whether a signature made with no connection is queued instead of lost.
export function isRetryableFailure(err) {
  return failureKind(err) !== 'rejected'
}

// Only "no response at all", the narrow sense. Kept for callers that mean
// exactly that; a sync should ask failureKind.
export function isNetworkError(err) {
  return failureKind(err) === 'offline'
}

// The server's own words for a refusal, when it gave any. A 422 arrives as a
// networkError whose body carries the GraphQL errors; Apollo's own message for
// it is only "Response not successful: Received status code 422".
export function failureMessage(err) {
  if (!err) return ''
  const gql = (err.graphQLErrors || []).find((e) => e && e.message)
  if (gql) return gql.message
  const body = err.networkError && err.networkError.result
  const errors = (body && body.errors) || []
  const first = errors.find((e) => e && e.message)
  if (first) return first.message
  return String(err.message || err)
}

// All queued items, ordered FIFO. Pass `locationId` to restrict to the items
// tagged for one downloaded location (the Infrared Offline per-location sync).
// Whether an item belongs to the feature being drained. Items queued before
// the tag existed carry no feature, they are all Infrared Offline's (the only
// feature shipping then), so untagged matches 'infrared'. Without this rule, a
// LOTO sync of location N would drain the INFRARED queue for N too, the two
// features can download the same location.
// WHEN A LOTO CHANGE WAS MADE, for one queued offline (Angel, 2026-10-23): the
// LOTO builders stamp madeAt on the input as each change is queued
// (loto-offline-mutations.js); one queued by an older app has none, and gets
// the time it was queued. LOTO only: every LOTO input the outbox can hold takes
// madeAt, and no infrared one does, so adding it there would be refused.
export function withMadeAt(item, variables) {
  if (!item || item.feature !== 'loto' || !item.createdAt) return variables
  const input = variables && variables.input
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return variables
  }
  if (input.madeAt) return variables
  return {
    ...variables,
    input: { ...input, madeAt: new Date(item.createdAt).toISOString() },
  }
}

export function matchesFeature(item, feature) {
  if (feature == null) return true
  return item.feature === feature || (!item.feature && feature === 'infrared')
}

// Whether an item belongs to the location being drained. Compared as strings:
// the same site arrives as "321193" from the location picker (a GraphQL ID)
// and as 321193 from the devices page. A strict === between the two matched
// nothing, so a download made on one page and synced from the other sent no
// changes at all, then reported the location synced and deleted it, leaving
// every change stranded in the queue. The same mismatch let a re-download
// through the guard that exists to stop it.
export function matchesLocation(item, locationId) {
  if (locationId == null) return true
  return (
    item.locationId != null && String(item.locationId) === String(locationId)
  )
}

export async function allItems({ locationId, feature } = {}) {
  const items = []
  await store.iterate((value) => {
    items.push(value)
  })
  const filtered = items.filter(
    (i) => matchesLocation(i, locationId) && matchesFeature(i, feature)
  )
  return filtered.sort((a, b) => a.seq - b.seq)
}

// Drop every queued change one feature holds for one location: removing a
// download discards its unsynced work, as the remove dialog tells the tech it
// will. Left behind, the changes stayed counted in the app-bar chip with no
// download to show, inspect or sync them from. An unscoped call is refused
// rather than taken as "empty the whole queue".
export async function removeForLocation({ locationId, feature } = {}) {
  if (locationId == null) return 0
  const items = await allItems({ locationId, feature })
  for (const item of items) {
    await store.removeItem(item.id)
  }
  if (items.length) notifyOfflineQueues()
  return items.length
}

// Seed the sequence counter from any queue persisted across restarts.
export async function initSeq() {
  const items = await allItems()
  seqCounter = items.reduce((max, i) => Math.max(max, i.seq || 0), 0)
}

export async function enqueue({
  query,
  variables,
  operationName,
  baseVersion,
  locationId,
  feature,
  tempId,
  idResponsePath,
  idResponsePaths,
}) {
  const queued = await store.length()
  if (queued >= MAX_QUEUED_MUTATIONS) {
    const err = new Error(
      `Offline queue is full (${MAX_QUEUED_MUTATIONS}). Reconnect to sync before making more changes.`
    )
    reportError(err, { source: 'outbox', operationName })
    throw err
  }
  const id = uuid()
  seqCounter += 1
  // JSON round-trip before storing: callers pass variables straight out of Vue
  // components, where nested objects are reactive proxies. And IndexedDB's
  // structured clone refuses a Proxy ("DataCloneError"). Mutation variables
  // are pure JSON by construction, so nothing is lost.
  let vars = JSON.parse(JSON.stringify(variables || {}))
  if (offlineSyncConfig.sendIdempotencyKey) {
    vars = injectField(vars, 'idempotencyKey', id)
  }
  const item = {
    id,
    seq: seqCounter,
    operationName: operationName || null,
    query,
    variables: vars,
    idempotencyKey: id,
    // The entity version this change was based on. Sent for optimistic
    // concurrency (when the feature flag is on) and used by "keep mine"
    // conflict resolution to overwrite past the server's current version.
    baseVersion: baseVersion == null ? null : baseVersion,
    // Which downloaded location this change belongs to; null for untagged
    // items. Lets Sync drain one location at a time.
    locationId: locationId == null ? null : locationId,
    // Which offline feature queued it ('infrared' | 'loto'); null = legacy
    // item from before the tag, treated as infrared (see matchesFeature).
    feature: feature == null ? null : feature,
    // Creates that other queued mutations reference by placeholder: the
    // `pending:` id this item was known by locally, and where in the mutation
    // response the real id lands (dot-path, e.g.
    // 'addInfraredSurvey.infraredsurvey.id'). After a successful replay the
    // temp→real mapping is registered so dependents reconcile.
    tempId: tempId || null,
    idResponsePath: idResponsePath || null,
    // { tempId: path } for a create that makes several things at once.
    idResponsePaths: idResponsePaths || null,
    createdAt: Date.now(),
    attempts: 0,
    status: 'pending',
    error: null,
  }
  await store.setItem(id, item)
  notifyOfflineQueues()
  return item
}

// Replace a queued item's variables in place (e.g. dropping one photo from a
// queued batch). JSON round-trips for the same Proxy/Date reasons as enqueue.
// `fields` sets other parts of the item with it: a merged change that now
// makes something new carries the idResponsePaths it is learned by.
export async function updateItemVariables(id, variables, fields = {}) {
  const item = await store.getItem(id)
  if (!item) return null
  Object.assign(item, fields)
  item.variables = JSON.parse(JSON.stringify(variables))
  await store.setItem(id, item)
  notifyOfflineQueues()
  return item
}

export async function remove(id) {
  await store.removeItem(id)
  notifyOfflineQueues()
}

export async function count() {
  return store.length()
}

// Replay the queue FIFO against the given Apollo client.
//   - Success  → remove the item.
//   - Network  → stop immediately (still offline); order is preserved for the
//                next flush.
//   - CONFLICT → mark `conflict` (server rejected on optimistic-concurrency);
//                skip and surface for user resolution. Never auto-retried.
//   - LOCKED_OUT mark `blocked`: refused for a reason that clears by itself
//                (see isTemporaryRefusal). Left retryable, so the NEXT flush
//                sends it again with no human action.
//   - other    → mark `failed` and skip it so a single bad item can't wedge
//     GraphQL   everything behind it; it stays for inspection/retry.
// `onChange` (optional) is awaited after each item so the UI can refresh.
// `locationId` (optional) drains only the items tagged for that location.
export async function flush(
  client,
  { onChange, onProgress, locationId, feature } = {}
) {
  const items = await allItems({ locationId, feature })
  const idMap = await loadIdMap()
  const result = {
    synced: 0,
    failed: 0,
    conflicts: 0,
    // Refused for a reason that clears on its own; retried automatically.
    blocked: 0,
    held: 0,
    // Stopped early without sending everything, and why (failureKind's
    // offline / stalled / auth / server). Everything not sent stays queued.
    stoppedOffline: false,
    stoppedReason: null,
  }
  // Only the items this flush can actually try, so the progress reads "3 of
  // 7" against work it will do rather than items parked for a person.
  const sendable = items.filter(
    (item) => item.status !== 'failed' && item.status !== 'conflict'
  ).length
  let attempted = 0
  if (onProgress && sendable) onProgress({ done: 0, total: sendable })
  for (const item of items) {
    // Don't auto-re-run items awaiting a human decision. `blocked` is
    // deliberately NOT in this list: nobody has a decision to make about it,
    // and retrying is the only way it can ever go through.
    if (item.status === 'failed' || item.status === 'conflict') continue
    attempted += 1
    // Patch any temp ids (e.g. a `pending:` storeId) with the real ones learned
    // from a dependency that has since synced.
    let variables = reconcileValue(item.variables, idMap)
    // If a dependency still hasn't resolved, hold this item for a later flush
    // rather than sending a placeholder id the server would reject.
    if (hasUnresolvedTempId(variables)) {
      result.held += 1
      continue
    }
    // A LOTO change queued before the app stamped madeAt on it still says when
    // it was made: when it was queued (see withMadeAt).
    variables = withMadeAt(item, variables)
    // Optimistic-concurrency: attach the version this change was based on so the
    // server can reject a conflicting concurrent edit. Flag-gated because the
    // input field doesn't exist until the backend adds it.
    if (offlineSyncConfig.sendExpectedVersion && item.baseVersion != null) {
      variables = injectField(
        variables,
        offlineSyncConfig.expectedVersionField,
        item.baseVersion
      )
    }
    try {
      const { data } = await client.mutate({
        mutation: gql`
          ${item.query}
        `,
        variables,
        fetchPolicy: 'no-cache',
      })
      // A create that dependents referenced by placeholder: learn the real id
      // from the response and register the temp→real mapping BEFORE moving on,
      // so a child queued behind this item reconciles on this same flush.
      if (item.tempId && item.idResponsePath) {
        const realId = readPath(data, item.idResponsePath)
        if (realId != null) {
          await registerId(item.tempId, realId)
          idMap[item.tempId] = realId
        }
      }
      for (const [tempId, path] of Object.entries(item.idResponsePaths || {})) {
        const realId = readPath(data, path)
        if (realId != null) {
          await registerId(tempId, realId)
          idMap[tempId] = realId
        }
      }
      await remove(item.id)
      result.synced += 1
    } catch (err) {
      const kind = failureKind(err)
      if (kind !== 'rejected') {
        // Nothing wrong with the change. Stop here, in order, and keep it.
        result.stoppedOffline = true
        result.stoppedReason = kind
        break
      }
      item.attempts += 1
      item.error = failureMessage(err)
      if (graphQLErrorCode(err) === 'CONFLICT') {
        // Someone changed the record while we were offline. Park it for the
        // user to resolve (keep mine / discard). Don't overwrite silently.
        item.status = 'conflict'
        item.conflict = conflictInfo(err)
        await store.setItem(item.id, item)
        result.conflicts += 1
      } else if (isTemporaryRefusal(err)) {
        // Hold it, don't park it. Nothing is wrong with the change and nobody
        // can act on this, so it is not reported either: an error report per
        // queued edit per flush, for the whole time a machine is locked out,
        // is noise that would bury the failures that do need a person.
        item.status = 'blocked'
        await store.setItem(item.id, item)
        result.blocked += 1
      } else {
        // A mutation the server actively rejected (validation, etc.) needs a
        // human. Surface it rather than retrying forever.
        item.status = 'failed'
        await store.setItem(item.id, item)
        result.failed += 1
        reportError(err, {
          source: 'outbox',
          operationName: item.operationName,
          idempotencyKey: item.idempotencyKey,
        })
      }
    }
    notifyOfflineQueues()
    if (onProgress) onProgress({ done: attempted, total: sendable })
    if (onChange) await onChange()
  }
  return result
}

// The real id a create's temp id turned into, once that create has synced, or
// null. Lets an offline editor whose snapshot still shows a temp id (a partial
// sync) delete the server's record instead of a queued create that is gone.
export async function lookupId(tempId) {
  const real = await idMapStore.getItem(tempId)
  return real == null ? null : real
}

// Resolve a parked conflict.
//   - 'discard'  → drop the queued change, keeping the server's version.
//   - 'keep-mine' → re-send, overwriting: adopt the server's current version as
//     the new base so the optimistic-concurrency check passes, then set pending.
export async function resolveConflict(id, resolution) {
  const item = await store.getItem(id)
  if (!item || item.status !== 'conflict') return
  if (resolution === 'discard') {
    await store.removeItem(id)
    notifyOfflineQueues()
    return
  }
  if (resolution === 'keep-mine') {
    if (item.conflict && item.conflict.currentVersion != null) {
      item.baseVersion = item.conflict.currentVersion
    }
    item.status = 'pending'
    item.error = null
    item.conflict = null
    await store.setItem(id, item)
    notifyOfflineQueues()
  }
}

// Reset previously-failed items to pending so the next flush retries them.
// Scoped by locationId when given (a per-location Sync is an explicit human
// retry of everything in that location, including past rejections).
export async function resetFailed({ locationId, feature } = {}) {
  const items = await allItems({ locationId, feature })
  for (const item of items) {
    if (item.status === 'failed') {
      item.status = 'pending'
      item.error = null
      await store.setItem(item.id, item)
    }
  }
  notifyOfflineQueues()
}

// Manual retry of items that previously failed with a permanent error (e.g.
// after the user resolves a conflict). Resets them to pending, then flushes.
export async function retryFailed(client, opts) {
  await resetFailed(opts || {})
  return flush(client, opts)
}

// Drop only the permanently-failed items (e.g. after the user acknowledges a
// rejection they can't resolve). Leaves pending items untouched.
export async function clearFailed() {
  const items = await allItems()
  for (const item of items) {
    if (item.status === 'failed') await store.removeItem(item.id)
  }
}

export async function clear() {
  await store.clear()
  await idMapStore.clear()
  seqCounter = 0
}
